import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";

const emptyItem = () => ({
    _key: Math.random().toString(36).slice(2, 10),
    description: "",
    category: "",
    quantity: 1,
    unit: "u",
    unit_price: 0,
    total: 0,
    tags: [],
});

export default function PublicQuotePage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        contractor_name: "",
        trade: "",
        contractor_email: "",
        contractor_phone: "",
        reference: "",
        tva_rate: 20,
        notes: "",
    });
    const [items, setItems] = useState([emptyItem()]);

    useEffect(() => {
        (async () => {
            try {
                const data = await api.getPublicQuote(token);
                setInfo(data);
                setForm((f) => ({
                    ...f,
                    contractor_name: data.link.contractor_name || "",
                    trade: data.link.trade || "",
                }));
                if (data.link.submitted) setSubmitted(true);
            } catch {
                toast.error("Lien invalide ou expiré");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const updateItem = (idx, key, value) => {
        const next = items.map((it, i) =>
            i === idx ? { ...it, [key]: value } : it
        );
        if (key === "quantity" || key === "unit_price") {
            next[idx].total = parseFloat(
                (
                    parseFloat(next[idx].quantity || 0) *
                    parseFloat(next[idx].unit_price || 0)
                ).toFixed(2)
            );
        }
        setItems(next);
    };

    const submit = async () => {
        if (!form.contractor_name.trim() || !form.trade.trim()) {
            toast.error("Renseignez votre nom et corps de métier");
            return;
        }
        const cleanedItems = items
            .filter((it) => it.description.trim())
            .map((it) => ({
                ...it,
                quantity: parseFloat(it.quantity) || 0,
                unit_price: parseFloat(it.unit_price) || 0,
                total:
                    parseFloat(it.total) ||
                    parseFloat(it.quantity) * parseFloat(it.unit_price) ||
                    0,
                tags: it.tags || [],
            }));
        if (cleanedItems.length === 0) {
            toast.error("Ajoutez au moins un poste");
            return;
        }
        setSubmitting(true);
        try {
            await api.submitPublicQuote(token, {
                contractor_name: form.contractor_name.trim(),
                trade: form.trade.trim().toLowerCase(),
                contractor_email: form.contractor_email || null,
                contractor_phone: form.contractor_phone || null,
                reference: form.reference || null,
                tva_rate: parseFloat(form.tva_rate) || 20,
                notes: form.notes || null,
                line_items: cleanedItems,
            });
            setSubmitted(true);
            toast.success("Devis envoyé !");
        } catch (e) {
            toast.error("Erreur d'envoi");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center text-ink-soft">
                Chargement…
            </div>
        );
    if (!info)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="panel p-10 text-center">
                    <div className="font-heading text-xl mb-2">
                        Lien invalide
                    </div>
                    <p className="text-ink-soft text-sm">
                        Ce lien n'existe pas ou a expiré.
                    </p>
                </div>
            </div>
        );

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div
                    className="panel p-10 text-center max-w-md"
                    data-testid="public-submitted"
                >
                    <CheckCircle2
                        className="w-12 h-12 mx-auto text-ok mb-4"
                        strokeWidth={1.5}
                    />
                    <div className="font-heading text-2xl font-bold mb-2">
                        Devis envoyé !
                    </div>
                    <p className="text-ink-soft text-sm">
                        Merci. Le promoteur a bien reçu votre devis pour le
                        chantier <b>{info.project.name}</b>. Il vous
                        contactera pour la suite.
                    </p>
                </div>
            </div>
        );
    }

    const total = items.reduce((acc, it) => acc + (parseFloat(it.total) || 0), 0);

    return (
        <div className="min-h-screen py-10 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="border-l-4 border-accent pl-4 mb-8">
                    <div className="stat-label">Saisie de devis · AB Promotion</div>
                    <h1 className="font-heading text-3xl font-extrabold mt-1">
                        {info.project.name}
                    </h1>
                    {info.project.address && (
                        <div className="text-sm text-ink-soft mt-1 flex items-center gap-1.5">
                            <Building2
                                className="w-3.5 h-3.5"
                                strokeWidth={1.6}
                            />
                            {info.project.address}
                        </div>
                    )}
                    {info.project.description && (
                        <p className="text-sm text-ink-soft mt-2 max-w-2xl">
                            {info.project.description}
                        </p>
                    )}
                </div>

                <section className="panel p-6 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="stat-label">
                                Votre entreprise *
                            </Label>
                            <Input
                                data-testid="public-input-name"
                                className="rounded-none mt-1.5"
                                value={form.contractor_name}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        contractor_name: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <Label className="stat-label">
                                Corps de métier *
                            </Label>
                            <Input
                                data-testid="public-input-trade"
                                placeholder="plombier, électricien, terrassier…"
                                className="rounded-none mt-1.5"
                                value={form.trade}
                                onChange={(e) =>
                                    setForm({ ...form, trade: e.target.value })
                                }
                            />
                        </div>
                        <div>
                            <Label className="stat-label">Email</Label>
                            <Input
                                className="rounded-none mt-1.5"
                                value={form.contractor_email}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        contractor_email: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <Label className="stat-label">Téléphone</Label>
                            <Input
                                className="rounded-none mt-1.5"
                                value={form.contractor_phone}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        contractor_phone: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <Label className="stat-label">N° devis</Label>
                            <Input
                                className="rounded-none mt-1.5"
                                value={form.reference}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        reference: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <Label className="stat-label">TVA (%)</Label>
                            <Input
                                type="number"
                                className="rounded-none mt-1.5 num"
                                value={form.tva_rate}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        tva_rate: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="stat-label">
                                Détail des postes
                            </Label>
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-none border-ink"
                                onClick={() =>
                                    setItems([...items, emptyItem()])
                                }
                                data-testid="public-add-item"
                            >
                                <Plus className="w-3.5 h-3.5 mr-1" />
                                Ajouter une ligne
                            </Button>
                        </div>
                        <div className="border border-border">
                            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#FAFAFA] border-b border-border text-[11px] uppercase tracking-wide text-ink-soft font-medium">
                                <div className="col-span-5">Désignation</div>
                                <div className="col-span-1 text-right">Qté</div>
                                <div className="col-span-1">Unité</div>
                                <div className="col-span-2 text-right">PU HT</div>
                                <div className="col-span-2 text-right">
                                    Total HT
                                </div>
                                <div className="col-span-1"></div>
                            </div>
                            {items.map((it, idx) => (
                                <div
                                    key={it._key || idx}
                                    className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-0 items-center"
                                >
                                    <Input
                                        className="rounded-none col-span-5 h-9"
                                        placeholder="Description du poste"
                                        value={it.description}
                                        onChange={(e) =>
                                            updateItem(
                                                idx,
                                                "description",
                                                e.target.value
                                            )
                                        }
                                        data-testid={`public-item-desc-${idx}`}
                                    />
                                    <Input
                                        type="number"
                                        className="rounded-none col-span-1 h-9 num text-right"
                                        value={it.quantity}
                                        onChange={(e) =>
                                            updateItem(
                                                idx,
                                                "quantity",
                                                e.target.value
                                            )
                                        }
                                    />
                                    <Input
                                        className="rounded-none col-span-1 h-9"
                                        value={it.unit}
                                        onChange={(e) =>
                                            updateItem(idx, "unit", e.target.value)
                                        }
                                    />
                                    <Input
                                        type="number"
                                        className="rounded-none col-span-2 h-9 num text-right"
                                        value={it.unit_price}
                                        onChange={(e) =>
                                            updateItem(
                                                idx,
                                                "unit_price",
                                                e.target.value
                                            )
                                        }
                                    />
                                    <div className="col-span-2 text-right num text-sm font-semibold">
                                        {(parseFloat(it.total) || 0).toFixed(2)} €
                                    </div>
                                    <button
                                        className="col-span-1 text-ink-soft hover:text-danger"
                                        onClick={() =>
                                            setItems(
                                                items.filter((_, i) => i !== idx)
                                            )
                                        }
                                    >
                                        <Trash2
                                            className="w-4 h-4"
                                            strokeWidth={1.6}
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-end gap-3 mt-3 num">
                            <span className="stat-label">Total HT</span>
                            <span className="text-xl font-heading font-bold">
                                {fmtEUR(total)}
                            </span>
                        </div>
                    </div>

                    <div>
                        <Label className="stat-label">
                            Commentaires / précisions
                        </Label>
                        <Textarea
                            className="rounded-none mt-1.5"
                            rows={3}
                            value={form.notes}
                            onChange={(e) =>
                                setForm({ ...form, notes: e.target.value })
                            }
                        />
                    </div>

                    <Button
                        data-testid="public-submit"
                        className="rounded-none bg-ink text-white hover:bg-ink/90 w-full"
                        onClick={submit}
                        disabled={submitting}
                    >
                        {submitting ? "Envoi…" : "Envoyer le devis au promoteur"}
                    </Button>
                </section>
            </div>
        </div>
    );
}
